/**
 * DESIGN DEMO - Profesyonel UI/UX Redesign Preview
 * 
 * Bu component, yeni tasarım sisteminin demo'sunu gösterir.
 * Mevcut sistem değiştirilmeden önce bu demo ile görsel olarak değerlendirme yapılabilir.
 * 
 * Yeni Tasarım Prensipleri:
 * - Emlak & Oto sektörüne özel renk paleti
 * - Güven veren, profesyonel görünüm
 * - Hızlı kayıt ve işlem odaklı UX
 * - Modern ama klasik denge
 */

import React, { useState } from 'react';
import { Home, Car, ArrowRight, ShieldCheck, Clock, ThumbsUp, MessageCircle, Mail, FileText, Check, X, Eye, EyeOff, Building, User, Lock, Phone, MapPin, CreditCard, CheckCircle } from 'lucide-react';

interface DesignDemoProps {
  onClose?: () => void;
}

const DesignDemo: React.FC<DesignDemoProps> = ({ onClose }) => {
  const [activeView, setActiveView] = useState<'hero' | 'auth' | 'components'>('hero');
  const [showPassword, setShowPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // YENİ RENK PALETİ - Profesyonel Emlak & Oto Pazar Yeri
  // Primary: Deep Navy (Güven, Profesyonellik, Emlak)
  // Secondary: Vibrant Orange (Enerji, Hareket, Oto)
  // Accent: Emerald Green (Büyüme, Başarı)
  // Neutral: Warm Grays (Sıcak, Yaklaşılabilir)

  const colorPalette = {
    primary: {
      50: '#E8EDF4',
      100: '#C3D4E4',
      200: '#9AB6D1',
      300: '#7198BD',
      400: '#5282AE',
      500: '#336C9F', // Deep Navy - Primary
      600: '#2A5A85',
      700: '#21486B',
      800: '#183651',
      900: '#0F2437',
    },
    secondary: {
      50: '#FFF4E6',
      100: '#FFE4B3',
      200: '#FFD280',
      300: '#FFC04D',
      400: '#FFB31A',
      500: '#FF8C00', // Vibrant Orange - Secondary
      600: '#E67E00',
      700: '#CC7000',
      800: '#B36200',
      900: '#994D00',
    },
    accent: {
      50: '#E6F7F0',
      100: '#B3E8D1',
      200: '#80D9B2',
      300: '#4DCA93',
      400: '#1ABB74',
      500: '#10B981', // Emerald Green - Success/Accent
      600: '#0EA371',
      700: '#0C8D61',
      800: '#0A7751',
      900: '#086141',
    },
    neutral: {
      50: '#FAFAF9',
      100: '#F5F5F4',
      200: '#E7E5E4',
      300: '#D6D3D1',
      400: '#A8A29E',
      500: '#78716C',
      600: '#57534E',
      700: '#44403C',
      800: '#292524',
      900: '#1C1917',
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
      {/* Demo Header */}
      <div className={`border-b ${isDarkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'} sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg"
                  style={{ background: 'linear-gradient(to bottom right, #336C9F, #21486B)' }}
                >
                  <Home className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Tasarım Demo</h1>
                  <p className="text-xs text-neutral-500">Yeni Profesyonel Tasarım Sistemi</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* View Switcher */}
              <div className="flex items-center gap-2 p-1 rounded-lg bg-neutral-100 dark:bg-neutral-800">
                <button
                  onClick={() => setActiveView('hero')}
                  style={activeView === 'hero' ? { backgroundColor: isDarkMode ? '#44403C' : 'white', color: '#336C9F' } : {}}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeView === 'hero'
                      ? 'shadow-sm'
                      : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}
                >
                  Hero
                </button>
                <button
                  onClick={() => setActiveView('auth')}
                  style={activeView === 'auth' ? { backgroundColor: isDarkMode ? '#44403C' : 'white', color: '#336C9F' } : {}}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeView === 'auth'
                      ? 'shadow-sm'
                      : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}
                >
                  Auth
                </button>
                <button
                  onClick={() => setActiveView('components')}
                  style={activeView === 'components' ? { backgroundColor: isDarkMode ? '#44403C' : 'white', color: '#336C9F' } : {}}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeView === 'components'
                      ? 'shadow-sm'
                      : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}
                >
                  Components
                </button>
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-neutral-800 text-yellow-400' : 'bg-neutral-100 text-neutral-600'
                  }`}
              >
                {isDarkMode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>

              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <X className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Demo Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'hero' && (
          <HeroDemo isDarkMode={isDarkMode} colorPalette={colorPalette} />
        )}

        {activeView === 'auth' && (
          <AuthDemo isDarkMode={isDarkMode} colorPalette={colorPalette} showPassword={showPassword} setShowPassword={setShowPassword} />
        )}

        {activeView === 'components' && (
          <ComponentsDemo isDarkMode={isDarkMode} colorPalette={colorPalette} />
        )}
      </div>
    </div>
  );
};

// HERO SECTION DEMO
const HeroDemo: React.FC<{ isDarkMode: boolean; colorPalette: any }> = ({ isDarkMode, colorPalette }) => {
  return (
    <div className="space-y-12">
      <div className="text-center mb-8">
        <h2 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>
          Hero Section - Yeni Tasarım
        </h2>
        <p className="text-neutral-500">Profesyonel, güven veren, modern</p>
      </div>

      <div className={`relative min-h-[600px] rounded-3xl overflow-hidden ${isDarkMode ? 'bg-neutral-800' : 'bg-gradient-to-br from-neutral-50 to-white'} border ${isDarkMode ? 'border-neutral-700' : 'border-neutral-200'} shadow-2xl`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: '#336C9F' }}
          ></div>
          <div
            className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: '#FF8C00' }}
          ></div>
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center min-h-[600px] p-8">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8 ${isDarkMode ? 'bg-neutral-700 border border-neutral-600' : 'bg-white border border-neutral-200'} shadow-lg`}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#10B981' }}></div>
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-neutral-200' : 'text-neutral-700'}`}>
              Yeni Nesil Pazar Yeri
            </span>
          </div>

          {/* Main Headline - Daha profesyonel, daha az gradient */}
          <h1 className={`text-5xl md:text-7xl font-black text-center mb-6 leading-tight ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>
            Sen İste, <br />
            <span style={{ color: isDarkMode ? '#5282AE' : '#336C9F' }}>Teklif</span>{' '}
            <span className={isDarkMode ? 'text-white' : 'text-neutral-900'}>Cebine Gelsin</span>
          </h1>

          {/* Subtitle */}
          <p className={`text-xl md:text-2xl font-semibold mb-4 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-600'}`}>
            Ev ve Araba bulmanın en kolay yolu
          </p>

          {/* Description */}
          <p className={`text-base md:text-lg max-w-2xl text-center mb-12 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
            Emlak ve araç alım satımında aramakla vakit kaybetme. Kriterlerini gir, seçkin firmalardan en iyi teklifler sana gelsin.
          </p>

          {/* Category Cards - Daha şık, daha profesyonel */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl">
            {/* Emlak Card */}
            <div className={`group relative overflow-hidden rounded-2xl ${isDarkMode ? 'bg-neutral-700' : 'bg-white'} border ${isDarkMode ? 'border-neutral-600' : 'border-neutral-200'} shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer`}>
              <div
                className="absolute inset-0 to-transparent"
                style={{ background: 'linear-gradient(to bottom right, rgba(51, 108, 159, 0.1), transparent)' }}
              ></div>
              <div className="relative p-8">
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: '#336C9F' }}
                >
                  <Home className="w-8 h-8 text-white" />
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Emlak</h3>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Satılık veya Kiralık Konut, İşyeri, Arsa
                </p>
                <button
                  className="w-full py-3 px-6 rounded-xl font-semibold transition-all text-white flex items-center justify-center gap-2 group-hover:gap-3 hover:shadow-xl"
                  style={{ backgroundColor: '#336C9F' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2A5A85'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#336C9F'}
                >
                  Talep Oluştur
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Oto Card */}
            <div className={`group relative overflow-hidden rounded-2xl ${isDarkMode ? 'bg-neutral-700' : 'bg-white'} border ${isDarkMode ? 'border-neutral-600' : 'border-neutral-200'} shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer`}>
              <div
                className="absolute inset-0 to-transparent"
                style={{ background: 'linear-gradient(to bottom right, rgba(255, 140, 0, 0.1), transparent)' }}
              ></div>
              <div className="relative p-8">
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: '#FF8C00' }}
                >
                  <Car className="w-8 h-8 text-white" />
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Vasıta</h3>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  İkinci El ve Sıfır Araçlar
                </p>
                <button
                  className="w-full py-3 px-6 rounded-xl font-semibold transition-all text-white flex items-center justify-center gap-2 group-hover:gap-3 hover:shadow-xl"
                  style={{ backgroundColor: '#FF8C00' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E67E00'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
                >
                  Talep Oluştur
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Color Palette Display */}
      <div className={`mt-12 p-8 rounded-2xl ${isDarkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'} border`}>
        <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Yeni Renk Paleti</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-semibold text-neutral-700 dark:text-neutral-300 mb-3">Primary (Deep Navy)</h4>
            <div className="flex gap-2">
              {Object.values(colorPalette.primary).slice(4, 7).map((color, i) => (
                <div key={i} className="flex-1">
                  <div className="h-16 rounded-lg" style={{ backgroundColor: color as string }}></div>
                  <p className="text-xs text-neutral-500 mt-1">{Object.keys(colorPalette.primary)[i + 4]}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-neutral-700 dark:text-neutral-300 mb-3">Secondary (Orange)</h4>
            <div className="flex gap-2">
              {Object.values(colorPalette.secondary).slice(4, 7).map((color, i) => (
                <div key={i} className="flex-1">
                  <div className="h-16 rounded-lg" style={{ backgroundColor: color as string }}></div>
                  <p className="text-xs text-neutral-500 mt-1">{Object.keys(colorPalette.secondary)[i + 4]}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-neutral-700 dark:text-neutral-300 mb-3">Accent (Emerald)</h4>
            <div className="flex gap-2">
              {Object.values(colorPalette.accent).slice(4, 7).map((color, i) => (
                <div key={i} className="flex-1">
                  <div className="h-16 rounded-lg" style={{ backgroundColor: color as string }}></div>
                  <p className="text-xs text-neutral-500 mt-1">{Object.keys(colorPalette.accent)[i + 4]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// AUTH MODAL DEMO - Hızlı kayıt odaklı
const AuthDemo: React.FC<{ isDarkMode: boolean; colorPalette: any; showPassword: boolean; setShowPassword: (val: boolean) => void }> = ({ isDarkMode, colorPalette, showPassword, setShowPassword }) => {
  return (
    <div className="space-y-12">
      <div className="text-center mb-8">
        <h2 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>
          Hızlı Kayıt & Giriş - Yeni Tasarım
        </h2>
        <p className="text-neutral-500">Minimum adım, maksimum hız</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Customer Quick Login - SMS Based */}
        <div className={`rounded-2xl p-8 ${isDarkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'} border shadow-xl`}>
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: '#336C9F' }}
            >
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Müşteri Girişi</h3>
              <p className="text-sm text-neutral-500">SMS ile hızlı giriş</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                Telefon Numarası
              </label>
              <div className="relative">
                <Phone className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
                <input
                  type="tel"
                  placeholder="(5XX) XXX-XX-XX"
                  className={`w-full pl-12 pr-4 py-3.5 rounded-xl border ${isDarkMode
                      ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-500 focus:border-primary-500'
                      : 'bg-neutral-50 border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:border-primary-500'
                    } outline-none transition-colors`}
                />
              </div>
            </div>

            <button
              className="w-full py-3.5 px-6 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl"
              style={{ backgroundColor: '#336C9F' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2A5A85'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#336C9F'}
            >
              SMS Kodu Gönder
            </button>

            <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <p className="text-xs text-center text-neutral-500 mb-3">Hesabın yok mu?</p>
              <button className={`w-full py-3 px-6 rounded-xl font-semibold border-2 ${isDarkMode
                  ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                  : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                } transition-all`}>
                Hemen Üye Ol
              </button>
            </div>
          </div>
        </div>

        {/* Provider Quick Register - Streamlined */}
        <div className={`rounded-2xl p-8 ${isDarkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'} border shadow-xl`}>
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: '#FF8C00' }}
            >
              <Building className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Kurumsal Üyelik</h3>
              <p className="text-sm text-neutral-500">3 adımda hızlı kayıt</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Firma Adı *
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-3.5 rounded-xl border ${isDarkMode
                      ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-500 focus:border-secondary-500'
                      : 'bg-neutral-50 border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:border-secondary-500'
                    } outline-none transition-colors`}
                  placeholder="Firma Adı"
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Yetkili Kişi *
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-3.5 rounded-xl border ${isDarkMode
                      ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-500 focus:border-secondary-500'
                      : 'bg-neutral-50 border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:border-secondary-500'
                    } outline-none transition-colors`}
                  placeholder="Ad Soyad"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                E-posta *
              </label>
              <div className="relative">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
                <input
                  type="email"
                  className={`w-full pl-12 pr-4 py-3.5 rounded-xl border ${isDarkMode
                      ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-500 focus:border-secondary-500'
                      : 'bg-neutral-50 border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:border-secondary-500'
                    } outline-none transition-colors`}
                  placeholder="firma@email.com"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                Şifre *
              </label>
              <div className="relative">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`w-full pl-12 pr-12 py-3.5 rounded-xl border ${isDarkMode
                      ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-500 focus:border-secondary-500'
                      : 'bg-neutral-50 border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:border-secondary-500'
                    } outline-none transition-colors`}
                  placeholder="En az 8 karakter"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-xl bg-accent-50 dark:bg-accent-500/10 border border-accent-200 dark:border-accent-500/20">
              <CheckCircle className="w-5 h-5 text-accent-600 dark:text-accent-400 mt-0.5" />
              <p className="text-sm text-neutral-700 dark:text-neutral-300">
                Detaylı bilgileri daha sonra tamamlayabilirsiniz. Hemen başlamak için temel bilgiler yeterli.
              </p>
            </div>

            <button
              className="w-full py-3.5 px-6 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl"
              style={{ backgroundColor: '#FF8C00' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E67E00'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
            >
              Hemen Başla
            </button>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className={`rounded-2xl p-8 ${isDarkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'} border`}>
        <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Hızlı Kayıt Özellikleri</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Clock, title: '60 Saniyede Kayıt', desc: 'Minimum bilgi ile hızlı üyelik' },
            { icon: ShieldCheck, title: 'Güvenli Platform', desc: 'Etbis kayıtlı, güvenilir sistem' },
            { icon: CreditCard, title: 'Anında Başla', desc: 'Kayıt sonrası hemen ilan verebilirsiniz' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: isDarkMode ? 'rgba(51, 108, 159, 0.2)' : 'rgba(51, 108, 159, 0.1)' }}
              >
                <item.icon className="w-6 h-6" style={{ color: '#336C9F' }} />
              </div>
              <div>
                <h4 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>{item.title}</h4>
                <p className="text-sm text-neutral-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// COMPONENTS DEMO
const ComponentsDemo: React.FC<{ isDarkMode: boolean; colorPalette: any }> = ({ isDarkMode, colorPalette }) => {
  return (
    <div className="space-y-12">
      <div className="text-center mb-8">
        <h2 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>
          Yeni Bileşenler
        </h2>
        <p className="text-neutral-500">Tutarlı, profesyonel, kullanıcı dostu</p>
      </div>

      {/* Buttons */}
      <div className={`rounded-2xl p-8 ${isDarkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'} border`}>
        <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Button Styles</h3>
        <div className="flex flex-wrap gap-4">
          <button
            className="px-6 py-3 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl"
            style={{ backgroundColor: '#336C9F' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2A5A85'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#336C9F'}
          >
            Primary Button
          </button>
          <button
            className="px-6 py-3 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl"
            style={{ backgroundColor: '#FF8C00' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E67E00'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
          >
            Secondary Button
          </button>
          <button className={`px-6 py-3 rounded-xl font-semibold border-2 ${isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-700' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
            } transition-all`}>
            Outline Button
          </button>
          <button
            className="px-6 py-3 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl"
            style={{ backgroundColor: '#10B981' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0EA371'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10B981'}
          >
            Success Button
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className={`rounded-2xl p-8 ${isDarkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'} border`}>
        <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Card Styles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`p-6 rounded-xl border ${isDarkMode ? 'bg-neutral-700 border-neutral-600' : 'bg-neutral-50 border-neutral-200'
              } hover:shadow-lg transition-all`}>
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                style={{ backgroundColor: isDarkMode ? 'rgba(51, 108, 159, 0.2)' : 'rgba(51, 108, 159, 0.1)' }}
              >
                <ShieldCheck className="w-6 h-6" style={{ color: '#336C9F' }} />
              </div>
              <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>Card Title {i}</h4>
              <p className="text-sm text-neutral-500">
                Profesyonel kart tasarımı örneği. Temiz, modern, kullanıcı dostu.
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesignDemo;